import type {
  WorkshopJobDetail,
  WorkshopQuoteTotals,
} from "../api/client";
import {
  WORKSHOP_QUOTE_STATUS_LABELS,
  formatWorkshopDateTime,
  formatWorkshopMoney,
} from "./workshop-labels";

export type WorkshopQuotePrintContext = {
  shopName?: string;
  branchName?: string | null;
};

export type WorkshopQuotePaperSize = "thermal80" | "a4";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function quoteStatusLabel(status: string): string {
  return (
    WORKSHOP_QUOTE_STATUS_LABELS[
      status as keyof typeof WORKSHOP_QUOTE_STATUS_LABELS
    ] ?? status
  );
}

function resolvePrintLines(
  job: WorkshopJobDetail,
  quote: WorkshopQuoteTotals,
): WorkshopQuotePrintLine[] {
  if (quote.printLines?.length) return quote.printLines;
  const lines: WorkshopQuotePrintLine[] = [];
  for (const p of job.parts ?? []) {
    lines.push({
      section: "part",
      name: p.name,
      qty: p.qty,
      amountText:
        p.source === "customer_supplied"
          ? "ລູກຄ້ານຳມາ"
          : formatWorkshopMoney(p.lineTotal, p.currency),
      excludedFromTotal: p.source === "customer_supplied",
    });
  }
  for (const l of job.labors ?? []) {
    lines.push({
      section: "labor",
      name: l.name,
      qty: l.qty,
      amountText: formatWorkshopMoney(l.lineTotal, l.currency),
    });
  }
  return lines;
}

type WorkshopQuotePrintLine = WorkshopQuoteTotals["printLines"][number];

function lineRows(
  quote: WorkshopQuoteTotals,
  job: WorkshopJobDetail,
): string {
  return resolvePrintLines(job, quote)
    .map(
      (line) => `<tr>
        <td>${escapeHtml(line.name)}${line.excludedFromTotal ? " <span class=\"muted\">(ລູກຄ້າ)</span>" : ""}</td>
        <td class="num">${line.qty}</td>
        <td class="num">${escapeHtml(line.amountText)}</td>
      </tr>`,
    )
    .join("");
}

function totalsBlock(quote: WorkshopQuoteTotals): string {
  const rows = [
    `<div class="row"><span class="k">ຄ່າອາໄຫຼ່</span><span class="v">${escapeHtml(formatWorkshopMoney(quote.partsSubtotal, quote.currency))}</span></div>`,
    `<div class="row"><span class="k">ຄ່າແຮງ</span><span class="v">${escapeHtml(formatWorkshopMoney(quote.laborsSubtotal, quote.currency))}</span></div>`,
  ];
  if (quote.taxTotal > 0.009) {
    rows.push(
      `<div class="row"><span class="k">VAT ${quote.taxPercent}%</span><span class="v">${escapeHtml(formatWorkshopMoney(quote.taxTotal, quote.currency))}</span></div>`,
    );
  }
  rows.push(
    `<div class="row total-big"><span class="k">ລວມ</span><span class="v">${escapeHtml(formatWorkshopMoney(quote.grandTotal, quote.currency))}</span></div>`,
  );
  return rows.join("");
}

function buildThermalBody(
  job: WorkshopJobDetail,
  quote: WorkshopQuoteTotals,
  ctx?: WorkshopQuotePrintContext,
): string {
  const shop = ctx?.shopName?.trim() || "PARTZYNC";
  const branch = ctx?.branchName?.trim();
  return `<div class="receipt">
  <p class="brand">${escapeHtml(shop)}</p>
  <p class="sub">${branch ? `${escapeHtml(branch)} · ` : ""}ໃບສະເໜີລາຄາ / Quote</p>
  <hr class="rule" />
  <div class="row"><span class="k">ໃບງານ</span><span class="v mono">${escapeHtml(job.jobNumber)}</span></div>
  <div class="row"><span class="k">ທະບຽນ</span><span class="v">${escapeHtml(job.plateNumber)}</span></div>
  <div class="row"><span class="k">ລູກຄ້າ</span><span class="v">${escapeHtml(job.customerName)}</span></div>
  <div class="row"><span class="k">ເບີ</span><span class="v mono">${escapeHtml(job.customerPhone)}</span></div>
  <div class="row"><span class="k">ວັນທີ</span><span class="v">${escapeHtml(formatWorkshopDateTime(job.updatedAt))}</span></div>
  <div class="row"><span class="k">ສະຖານະ</span><span class="v">${escapeHtml(quoteStatusLabel(quote.quoteStatus))}</span></div>
  ${job.complaint?.trim() ? `<p class="note">${escapeHtml(job.complaint.trim())}</p>` : ""}
  <h2>ລາຍການ</h2>
  <table class="lines">
    <thead><tr><th>ລາຍການ</th><th class="num">ຈຳ</th><th class="num">ລວມ</th></tr></thead>
    <tbody>${lineRows(quote, job)}</tbody>
  </table>
  ${totalsBlock(quote)}
  <p class="footer">ໃບສະເໜີນີ້ບໍ່ແມ່ນໃບຮັບເງິນ · PARTZYNC</p>
</div>`;
}

function buildA4Body(
  job: WorkshopJobDetail,
  quote: WorkshopQuoteTotals,
  ctx?: WorkshopQuotePrintContext,
): string {
  const shop = ctx?.shopName?.trim() || "PARTZYNC";
  const branch = ctx?.branchName?.trim();
  return `<div class="sheet">
  <div class="a4-topbar"></div>
  <header class="a4-head">
    <div>
      <p class="a4-brand">${escapeHtml(shop)}</p>
      ${branch ? `<p class="a4-sub">${escapeHtml(branch)}</p>` : ""}
    </div>
    <div class="a4-title-block">
      <h1>ໃບສະເໜີລາຄາ</h1>
      <p class="a4-en">Quotation</p>
    </div>
  </header>
  <div class="a4-meta">
    <div class="a4-meta-item"><dt>ເລກໃບງານ</dt><dd class="mono">${escapeHtml(job.jobNumber)}</dd></div>
    <div class="a4-meta-item"><dt>ທະບຽນ</dt><dd>${escapeHtml(job.plateNumber)}</dd></div>
    <div class="a4-meta-item"><dt>ລູກຄ້າ</dt><dd>${escapeHtml(job.customerName)} · ${escapeHtml(job.customerPhone)}</dd></div>
    <div class="a4-meta-item"><dt>ວັນທີ</dt><dd>${escapeHtml(formatWorkshopDateTime(job.updatedAt))}</dd></div>
    <div class="a4-meta-item"><dt>ສະຖານະ</dt><dd>${escapeHtml(quoteStatusLabel(quote.quoteStatus))}</dd></div>
  </div>
  ${job.complaint?.trim() ? `<p class="a4-note"><strong>ອາການ / ວຽກ:</strong> ${escapeHtml(job.complaint.trim())}</p>` : ""}
  <table class="a4-lines">
    <thead><tr><th>ລາຍການ</th><th class="num">ຈຳນວນ</th><th class="num">ລວມ</th></tr></thead>
    <tbody>${lineRows(quote, job)}</tbody>
  </table>
  <div class="a4-totals-wrap">${totalsBlock(quote)}</div>
  <p class="a4-legal">ໃບສະເໜີລາຄານີ້ໃช້ເພື່ອແຈ້ງລາຄາປະເມີນເທົ່ານັ້ນ — ບໍ່ແມ່ນໃບຮັກກັບເງິນ ຫຼື ໃບກຳກັບພາສີ.</p>
  <footer class="a4-foot">PARTZYNC · Syncing Parts. Powering Service.</footer>
</div>`;
}

const STYLES_THERMAL80 = `
@page { size: 80mm auto; margin: 3mm; }
* { box-sizing: border-box; }
body { font-family: ui-sans-serif, system-ui, "Noto Sans Lao", sans-serif; font-size: 11px; line-height: 1.35; color: #0f172a; margin: 0; }
.receipt { max-width: 74mm; margin: 0 auto; }
.brand { font-weight: 700; font-size: 13px; text-align: center; margin: 0 0 2mm; }
.sub { text-align: center; font-size: 10px; color: #475569; margin: 0 0 3mm; }
.rule { border: none; border-top: 1px dashed #94a3b8; margin: 3mm 0; }
.row { display: flex; justify-content: space-between; gap: 4px; margin: 1mm 0; }
.row .k { color: #64748b; }
.row .v { text-align: right; word-break: break-word; }
.mono { font-family: ui-monospace, monospace; font-size: 10px; }
.note { font-size: 10px; color: #475569; margin: 2mm 0; }
h2 { font-size: 10px; font-weight: 600; text-transform: uppercase; color: #64748b; margin: 4mm 0 2mm; }
table.lines { width: 100%; border-collapse: collapse; font-size: 10px; }
table.lines th { text-align: left; font-weight: 600; color: #64748b; padding: 2px 0; border-bottom: 1px solid #e2e8f0; }
table.lines td { vertical-align: top; padding: 3px 0; border-bottom: 1px solid #f1f5f9; }
td.num { text-align: right; white-space: nowrap; }
.muted { color: #94a3b8; font-size: 9px; }
.total-big { font-weight: 700; font-size: 14px; margin-top: 2mm; }
.footer { margin-top: 4mm; text-align: center; font-size: 9px; color: #94a3b8; }
`;

const STYLES_A4 = `
@page { size: A4 portrait; margin: 16mm; }
* { box-sizing: border-box; }
body { font-family: ui-sans-serif, system-ui, "Noto Sans Lao", sans-serif; font-size: 11pt; line-height: 1.45; color: #0f172a; margin: 0; }
.a4-topbar { height: 4px; background: #ea580c; border-radius: 2px; margin-bottom: 20px; }
.a4-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; margin-bottom: 24px; }
.a4-brand { font-weight: 700; font-size: 16pt; margin: 0; }
.a4-sub { color: #64748b; margin: 4px 0 0; font-size: 10pt; }
.a4-title-block { text-align: right; }
.a4-title-block h1 { margin: 0; font-size: 20pt; }
.a4-en { margin: 4px 0 0; color: #64748b; font-size: 11pt; }
.a4-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; margin-bottom: 20px; padding: 16px; background: #f8fafc; border-radius: 8px; }
.a4-meta-item dt { font-size: 9pt; color: #64748b; margin: 0; }
.a4-meta-item dd { margin: 2px 0 0; font-weight: 500; }
.a4-note { margin: 0 0 16px; padding: 12px; background: #fff7ed; border-radius: 8px; font-size: 10pt; }
table.a4-lines { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
table.a4-lines th { text-align: left; padding: 8px 6px; border-bottom: 2px solid #e2e8f0; font-size: 9pt; color: #64748b; }
table.a4-lines td { padding: 8px 6px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
td.num { text-align: right; white-space: nowrap; }
.muted { color: #94a3b8; font-size: 9pt; }
.a4-totals-wrap { max-width: 280px; margin-left: auto; margin-bottom: 24px; }
.a4-totals-wrap .row { display: flex; justify-content: space-between; padding: 4px 0; }
.a4-totals-wrap .total-big { font-size: 14pt; font-weight: 700; border-top: 2px solid #0f172a; padding-top: 8px; margin-top: 8px; }
.a4-legal { font-size: 9pt; color: #64748b; }
.a4-foot { margin-top: 32px; text-align: center; font-size: 9pt; color: #94a3b8; }
`;

function runPrintHtml(styles: string, body: string): void {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("title", "print");
  Object.assign(iframe.style, {
    position: "fixed",
    right: "0",
    bottom: "0",
    width: "0",
    height: "0",
    border: "none",
    opacity: "0",
    pointerEvents: "none",
  });
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;
  if (!doc || !win) {
    iframe.remove();
    return;
  }

  doc.open();
  doc.write(`<!DOCTYPE html><html lang="lo"><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<style>${styles}</style></head><body>${body}</body></html>`);
  doc.close();

  const cleanup = () => iframe.remove();
  setTimeout(() => {
    try {
      win.focus();
      win.print();
    } catch {
      /* ignore */
    }
    setTimeout(cleanup, 800);
  }, 50);
}

export function printWorkshopQuote(
  job: WorkshopJobDetail,
  quote: WorkshopQuoteTotals,
  ctx?: WorkshopQuotePrintContext,
  paper: WorkshopQuotePaperSize = "thermal80",
): void {
  if (resolvePrintLines(job, quote).length === 0) return;
  if (paper === "a4") {
    runPrintHtml(STYLES_A4, buildA4Body(job, quote, ctx));
  } else {
    runPrintHtml(STYLES_THERMAL80, buildThermalBody(job, quote, ctx));
  }
}
